import {
  type BookingClient,
  CalendarIntegration,
  type DeliveryMode,
  type Location,
  LocationKind,
  type LocationRef,
  MeetingMode,
  MeetingPlatformIntegration,
  type SchedulingEventType,
  SchedulingIntegrationState,
  type UserControls,
  type UserSchedulingSettings,
} from '../../../../models/Scheduling'
import { type CreateBookingPayload, type RescheduleBookingPayload } from '../../../../store/scheduling/dto'
import { clientDisplayName } from '../../shared/schedulingDisplay'
import { toLocalISODate } from '../../../../common/dateUtils'
import { type ScheduleFormValues } from '../scheduleFormSchema'

export const isCapable = (integration: CalendarIntegration | MeetingPlatformIntegration): boolean => {
  return integration.connected && integration.state === SchedulingIntegrationState.LIVE && integration.capable
}

export const getSelectedCalendarIntegration = (settings: UserSchedulingSettings): CalendarIntegration | undefined => {
  const selected = settings.integrations.selectedCalendar
  return selected ? settings.integrations.calendars.find((candidate) => candidate.provider === selected) : undefined
}

export const hasBookableCalendar = (settings: UserSchedulingSettings): boolean => {
  const calendarIntegration = getSelectedCalendarIntegration(settings)
  return calendarIntegration != null && isCapable(calendarIntegration)
}

export const getSelectedMeetingPlatformIntegration = (settings: UserSchedulingSettings): MeetingPlatformIntegration | undefined => {
  const selected = settings.integrations.selectedMeetingPlatform
  return selected ? settings.integrations.meetingPlatforms.find((candidate) => candidate.provider === selected) : undefined
}

export const isMeetingPlatformBroken = (settings: UserSchedulingSettings): boolean => {
  const platformIntegration = getSelectedMeetingPlatformIntegration(settings)
  return platformIntegration ? !isCapable(platformIntegration) : false
}

export const suggestMeetingTitle = (clients: BookingClient[], ownerName: string, eventType: SchedulingEventType | null): string => {
  if (!eventType) {
    return ''
  }
  const clientNames = clients.map((client) => clientDisplayName(client)).join(', ')
  const lead = clientNames ? `${clientNames} x ${ownerName}` : ownerName
  return `${lead} - ${eventType.name}`
}

export type InviteeSource = 'team-member' | 'contact' | 'email'

export const inviteeRoleLabel = (source: InviteeSource, contactType?: string): string => {
  if (source === 'team-member') {
    return 'Team member'
  }
  if (source === 'email') {
    return 'Guest'
  }
  return contactType || 'Contact'
}

export type DefaultLocationKind = 'event-type' | 'account' | 'user'

export interface ResolvedDefaultLocation {
  kind: DefaultLocationKind
  locationId: string
  location: Location
}

interface DefaultLocationSources {
  eventType: SchedulingEventType | null
  accountDefaultLocationId?: string | null
  userDefaultLocationId?: string | null
}

export const resolveDefaultLocations = (sources: DefaultLocationSources, locations: Location[]): ResolvedDefaultLocation[] => {
  const byKind: { kind: DefaultLocationKind; locationId?: string | null }[] = [
    { kind: 'event-type', locationId: sources.eventType?.location?.id },
    { kind: 'account', locationId: sources.accountDefaultLocationId },
    { kind: 'user', locationId: sources.userDefaultLocationId },
  ]
  return byKind.flatMap(({ kind, locationId }) => {
    if (!locationId) {
      return []
    }
    const location = locations.find((candidate) => candidate.id === locationId)
    return location ? [{ kind, locationId, location }] : []
  })
}

export const isSymbolicLocationKind = (kind?: LocationKind): boolean => kind === LocationKind.ACCOUNT_DEFAULT || kind === LocationKind.USER_DEFAULT

export const defaultOptionLocationRef = (kind: DefaultLocationKind, eventType: SchedulingEventType, resolvedLocationId: string): LocationRef => {
  if (kind === 'account') {
    return { kind: LocationKind.ACCOUNT_DEFAULT, id: resolvedLocationId }
  }
  if (kind === 'user') {
    return { kind: LocationKind.USER_DEFAULT, id: resolvedLocationId }
  }
  return { kind: eventType.location?.kind ?? LocationKind.FIXED, id: resolvedLocationId }
}

export const bookingLocationFields = (meetingMode: MeetingMode, location: LocationRef | null): Pick<CreateBookingPayload, 'location'> => {
  if (meetingMode !== MeetingMode.IN_PERSON || !location) {
    return {}
  }
  if (isSymbolicLocationKind(location.kind)) {
    return { location: { kind: location.kind } }
  }
  return location.id ? { location: { kind: LocationKind.FIXED, id: location.id } } : {}
}

export const buildReschedulePayload = (bookingId: string, data: ScheduleFormValues, delivery: DeliveryMode): RescheduleBookingPayload => ({
  bookingId,
  start: data.timeMode === 'specific-time' ? data.slot?.startAt : undefined,
  suggestedDate: data.timeMode === 'client-chooses' && data.suggestedDate ? toLocalISODate(data.suggestedDate) : undefined,
  ...bookingLocationFields(data.meetingMode, data.location),
  note: data.note || undefined,
  delivery,
})

export const resolveLocationPreviewId = (
  location: LocationRef | null | undefined,
  accountDefaultLocationId: string | null | undefined,
  userDefaultLocationId: string | null | undefined,
): string | null => {
  if (location?.kind === LocationKind.ACCOUNT_DEFAULT) {
    return accountDefaultLocationId ?? null
  }
  if (location?.kind === LocationKind.USER_DEFAULT) {
    return userDefaultLocationId ?? null
  }
  return location?.id ?? null
}

export const canManageOwnLocations = (userControls: UserControls | undefined): boolean => userControls?.allowManageOwnLocations ?? true

export interface ViewTimezoneSources {
  isInPerson: boolean
  locationTimezone: string
  selectedTimezone: string
  userTimezone: string
}

export const resolveViewTimezone = ({ isInPerson, locationTimezone, selectedTimezone, userTimezone }: ViewTimezoneSources): string => {
  if (isInPerson && locationTimezone) {
    return locationTimezone
  }
  return selectedTimezone || userTimezone
}

export const countAffectedEventTypes = (
  eventTypes: Pick<SchedulingEventType, 'location'>[],
  current: Pick<Location, 'id'>,
  isChangeDefault: boolean,
  defaultKind?: LocationKind,
): number =>
  eventTypes.filter((eventType) => (isChangeDefault && defaultKind ? eventType.location?.kind === defaultKind : eventType.location?.id === current.id)).length
